import type { ServiceAreaContent } from "../../types/page-content";

interface Props {
  content: ServiceAreaContent;
  sectionNumber?: string;
}

export default function ServiceAreaSection({ content, sectionNumber = "06" }: Props) {
  return (
    <section className="bg-[#161616] py-28 lg:py-40 relative overflow-hidden">
      <div className="absolute right-6 top-10 hidden xl:block">
        <span className="font-heading text-white/[0.03] text-[180px] leading-none select-none">{sectionNumber}</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-14 mb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-0.5 bg-safety-500" />
              <span className="font-body text-safety-500 text-xs uppercase tracking-widest2 font-700">{content.eyebrow}</span>
            </div>
            <h2 className="font-heading text-white text-6xl lg:text-7xl tracking-display leading-none mb-6">
              {content.heading}
            </h2>
            <p className="font-body text-asphalt-300 text-sm leading-relaxed mb-10">
              {content.description}
            </p>

            <div className="grid grid-cols-2 gap-3 mb-10">
              {content.cities.map((city) => (
                <div key={city} className="flex items-center gap-3 border-b border-asphalt-700 pb-3">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-fill text-safety-500 text-sm" />
                  </div>
                  <span className="font-body text-asphalt-200 text-sm">{city}</span>
                </div>
              ))}
            </div>

            <div className="border-l-2 border-safety-500 pl-5 bg-[#111111] p-5">
              <p className="font-body text-asphalt-300 text-xs leading-relaxed">
                <strong className="text-white">Keywords: </strong>
                {content.seoText}
              </p>
            </div>
          </div>

          {/* Right */}
          <div className="flex flex-col">
            <div className="bg-[#111111] px-5 py-3 border border-asphalt-600 border-b-0 flex items-center gap-3">
              <div className="w-2 h-2 bg-safety-500 rounded-full" />
              <span className="font-body text-asphalt-200 text-xs uppercase tracking-widest font-700">
                Service Coverage — {content.primaryCity}
              </span>
            </div>
            <iframe
              title={`Service area map - ${content.primaryCity}`}
              src={content.mapSrc}
              width="100%"
              height="420"
              style={{ border: 0, display: "block" }}
              className="border border-asphalt-600"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>

      {/* Full-width map strip */}
      <div className="w-full">
        <iframe
          title={`Full coverage map - ${content.primaryCity} region`}
          src={content.mapSrc}
          width="100%"
          height="280"
          style={{ border: 0, display: "block", filter: "grayscale(30%) contrast(1.1)" }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <div className="bg-safety-500 py-3 px-14 flex items-center justify-between">
          <p className="font-body text-asphalt-950 text-xs uppercase tracking-widest font-700">
            Serving {content.primaryCity} &amp; surrounding communities
          </p>
          <a href="tel:5558007283" className="font-body text-asphalt-950 text-xs uppercase tracking-widest font-800 cursor-pointer whitespace-nowrap">
            (555) 800-7283
          </a>
        </div>
      </div>
    </section>
  );
}
